<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-chips" role="group" :aria-label="$t('order_status')">
        <button
          v-for="chip in statusChips"
          :key="chip.value"
          type="button"
          class="rd-chip"
          :aria-pressed="String(statusFilter === chip.value)"
          @click="statusFilter = chip.value"
        >
          {{ chip.label }}
          <b class="rd-num">{{ chip.count }}</b>
        </button>
      </div>
    </report-tools>

    <report-state v-if="loading && !report" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("worker") }} {{ sortArrow("name") }}</button>
            </th>
            <th v-if="hasExternalCode" scope="col">{{ $t("external_code") }}</th>
            <th v-if="isSingleDay" scope="col" class="rd-col-time" :title="$t('order_datetime')">{{ $t("time") }}</th>
            <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-center">
              <report-shift-tag :shift="shift.id"></report-shift-tag>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
            </th>
            <th scope="col" class="is-right rd-col-taken" :aria-sort="ariaSort('taken')">
              <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
              <button type="button" @click="sortBy('difference')">
                {{ $t("difference") }} {{ sortArrow("difference") }}
              </button>
            </th>
            <th scope="col">{{ $t("status") }}</th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row), 'is-idle': row.state === 'off' }"
            @click="openWorker(row, $event)"
            @keydown.enter.prevent="openWorker(row, $event)"
            @keydown.space.prevent="openWorker(row, $event)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td v-if="hasExternalCode" class="rd-num rd-muted">{{ row.externalCode || "–" }}</td>
            <td v-if="isSingleDay" class="rd-col-time rd-num rd-muted">{{ row.time || "–" }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center">
              <span v-if="row[shift.key]" class="rs-tag rs-tag--count" :data-shift="shift.id">
                {{ row[shift.key] }}
                <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: shift.id }) }}</span>
              </span>
              <template v-else>
                <span class="rd-dash" aria-hidden="true">–</span>
                <span class="rd-visually-hidden">0</span>
              </template>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordered }">{{ row.ordered }}</td>
            <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': row.difference === 0, 'is-alert': row.difference > 0 }">
              {{ row.difference }}
            </td>
            <td>
              <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
            </td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td v-if="hasExternalCode"></td>
            <td v-if="isSingleDay" class="rd-col-time"></td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center rd-num">{{ sums[shift.key] }}</td>
            <td class="is-right rd-num">{{ sums.ordered }}</td>
            <td class="is-right rd-num rd-col-taken">{{ sums.taken }}</td>
            <td class="is-right rd-num">{{ sums.difference }}</td>
            <td></td>
            <td class="rd-col-go"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabEmployees from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabEmployees.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import hasMoreEmployeeRows from "@/components/report/shared/more/hasMoreEmployeeRows";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/** Radnici koje računa stari tab (po izabranim restoranima i periodu), bez filtera statusa. */
const baseUsers = CompanyMultiRestaurantTabEmployees.computed.users;

/** Radnik ima narudžbu / preuzimanje: isti uslovi kao u starom filteru statusa (0 svi, 1–4 kao u listi). */
const matchesStatus = (user, status) => {
  const ordered = (user.order_count || 0) > 0;
  const taken = (user.order_payed || 0) > 0;
  switch (status) {
    case 1:
      return ordered;
    case 2:
      return !ordered;
    case 3:
      return ordered && taken;
    case 4:
      return ordered && !taken;
    default:
      return true;
  }
};

/**
 * Radnici firme "više restorana" na računaru: poručeno / preuzeto / razlika po radniku sa smenama u kojima je
 * poručio i statusom. Filter statusa su čipovi sa brojevima; klik na red otvara mesečni pregled radnika (kod svih
 * restorana) u panelu. Restorane, smene, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i
 * Excel iz starog taba (filter statusa je ovde, pa štampa i Excel prate ono što je na ekranu).
 */
export default {
  name: "CompanyDesktopMultiEmployees",
  extends: CompanyMultiRestaurantTabEmployees,
  mixins: [hasMultiFeed, hasMoreEmployeeRows, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    statusFilter: 0,
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    /** Svi radnici (stari tab ne filtrira po statusu dok je njegov orderStatus 0). */
    allUsers() {
      return baseUsers.call(this);
    },
    users() {
      return this.allUsers.filter((user) => matchesStatus(user, this.statusFilter));
    },
    statusChips() {
      const users = this.allUsers;
      const counts = [0, 1, 2, 3, 4].reduce((acc, status) => {
        acc[status] = users.filter((user) => matchesStatus(user, status)).length;
        return acc;
      }, {});
      return this.statusItems.map((item) => ({ value: item.value, label: item.text, count: counts[item.value] }));
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.allUsers.length ? this.$t("report_mobile.no_workers_in_view") : this.$t("no_employee_report_data");
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.taken },
        { key: "difference", label: this.$t("difference"), value: this.sums.difference },
      ];
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Radnik za mesečni pregled (isti podaci kao stari dijalog: "more-restaurants-orders-by-month"). */
    toEmployeeContext(row) {
      const company = this.$store.state.company;
      return {
        id: row.id,
        name: row.name,
        lastname: "",
        fullName: row.name,
        companyId: company && company.id,
        companyName: this.toLatin((company && company.name) || ""),
        monthUrl: "/user/more-restaurants-orders-by-month",
      };
    },
    openWorker(row, event) {
      const employee = this.toEmployeeContext(row);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { compact: true, worker: employee },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
